/**
 * Navigation pieces (design v2, D-104): tabs, sticky toolbar that turns to glass on scroll,
 * macOS-style sidebar sections. Router-agnostic: links are passed in by the app.
 */

import {
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

// ---------------------------------------------------------------- Tabs

export interface TabItem<T extends string> {
  value: T;
  label: string;
  /** Small counter next to the label. */
  count?: number;
  icon?: ReactNode;
}

export interface TabsProps<T extends string> {
  /** Accessible name of the tab list. */
  label: string;
  value: T;
  items: TabItem<T>[];
  onChange: (value: T) => void;
  /** Prefix for the tab/panel ids: use tabPanelProps(idPrefix, value) on each panel. */
  idPrefix: string;
}

export function tabPanelProps(idPrefix: string, value: string) {
  return {
    id: `${idPrefix}-panel-${value}`,
    role: "tabpanel" as const,
    "aria-labelledby": `${idPrefix}-tab-${value}`,
    tabIndex: 0,
  };
}

/** Underlined tabs (ARIA tabs pattern: arrow keys, Home, End). */
export function Tabs<T extends string>({ label, value, items, onChange, idPrefix }: TabsProps<T>) {
  const list = useRef<HTMLDivElement>(null);
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const index = items.findIndex((item) => item.value === value);
    const go = (i: number) => {
      const item = items[(i + items.length) % items.length];
      if (!item) return;
      onChange(item.value);
      document.getElementById(`${idPrefix}-tab-${item.value}`)?.focus();
    };
    if (event.key === "ArrowRight") go(index + 1);
    else if (event.key === "ArrowLeft") go(index - 1);
    else if (event.key === "Home") go(0);
    else if (event.key === "End") go(items.length - 1);
    else return;
    event.preventDefault();
  };
  return (
    <div ref={list} role="tablist" aria-label={label} className="ao-tabs">
      {items.map((item) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            id={`${idPrefix}-tab-${item.value}`}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={`${idPrefix}-panel-${item.value}`}
            tabIndex={selected ? 0 : -1}
            className="ao-tabs__tab"
            onClick={() => onChange(item.value)}
            onKeyDown={onKeyDown}
          >
            {item.icon}
            {item.label}
            {item.count !== undefined ? <span className="ao-badge">{item.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- Toolbar

/** True once the window (or `target`) has scrolled past `threshold` px. Passive listener. */
export function useScrolled(threshold = 4, target?: HTMLElement | null): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const source: HTMLElement | Window = target ?? window;
    const read = () => setScrolled((target ? target.scrollTop : window.scrollY) > threshold);
    read();
    source.addEventListener("scroll", read, { passive: true });
    return () => source.removeEventListener("scroll", read);
  }, [threshold, target]);
  return scrolled;
}

export interface ToolbarProps extends HTMLAttributes<HTMLElement> {
  /** Compact title shown only once the page has scrolled (the large title is out of view). */
  compactTitle?: ReactNode;
}

/** Sticky bar: transparent at the top, thin glass with a hairline once content scrolls under it. */
export function Toolbar({ compactTitle, className = "", children, ...rest }: ToolbarProps) {
  const scrolled = useScrolled();
  return (
    <header className={`ao-toolbar ${className}`.trim()} data-scrolled={scrolled} {...rest}>
      {compactTitle ? (
        <span className="ao-toolbar__title" aria-hidden={!scrolled}>
          {compactTitle}
        </span>
      ) : null}
      {children}
    </header>
  );
}

// ---------------------------------------------------------------- Sidebar

/** Class of a sidebar link (for router links: `className={({ isActive }) => sidebarItemClass(isActive)}`). */
export function sidebarItemClass(active: boolean): string {
  return active ? "ao-sidebar__item active" : "ao-sidebar__item";
}

export function SidebarSection({ title, children }: { title?: ReactNode; children: ReactNode }) {
  return (
    <div className="ao-sidebar__section">
      {title ? <p className="ao-sidebar__section-title">{title}</p> : null}
      {children}
    </div>
  );
}

export interface SidebarLinkProps extends HTMLAttributes<HTMLAnchorElement> {
  href: string;
  icon?: ReactNode;
  active?: boolean;
  count?: number;
}

/** Plain anchor version of a sidebar item (the back office uses its router's NavLink instead). */
export function SidebarLink({ href, icon, active = false, count, children, ...rest }: SidebarLinkProps) {
  return (
    <a href={href} className={sidebarItemClass(active)} aria-current={active ? "page" : undefined} {...rest}>
      {icon}
      <span>{children}</span>
      {count ? <span className="ao-count">{count}</span> : null}
    </a>
  );
}
